import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@/utils/helpers';
import { authAPI } from '@/services/api';
import { useAuthStore } from '@/store';

function getRedirectPath(): string {
  const redirect = sessionStorage.getItem('redirectAfterLogin');
  sessionStorage.removeItem('redirectAfterLogin');
  return redirect && redirect !== '/' ? redirect : '/';
}

export function LoginPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
  });

  // 自动检测 URL 中的 openId，验证有效则直接登录
  useEffect(() => {
    console.log('[DEBUG] Login.tsx useEffect 执行');
    console.log('[DEBUG] 当前 URL:', window.location.href);
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('openId');
    console.log('[DEBUG] URL 参数 openId:', openId ? openId.substring(0, 10) + '...' : '无');

    if (openId) {
      // 避免重复登录：如果本地已有相同 openId 的 token 且已认证，直接跳转
      const existingToken = localStorage.getItem('token');
      const storedOpenId = localStorage.getItem('openId');
      console.log('[DEBUG] 本地 token 存在?', !!existingToken, '本地 openId:', storedOpenId ? storedOpenId.substring(0, 10) + '...' : '无');

      if (existingToken && storedOpenId === openId) {
        console.log('[DEBUG] 已有相同 openId 的登录态，直接跳转首页');
        navigate('/');
        return;
      }

      console.log('[DEBUG] 开始调用 authAPI.openidLogin...');
      setIsLoading(true);
      setError('');

      authAPI.openidLogin(openId)
        .then((response) => {
          console.log('[DEBUG] authAPI.openidLogin 响应:', JSON.stringify(response));
          if (response.success && response.data) {
            console.log('[DEBUG] openId 登录成功，设置认证态并跳转');
            localStorage.setItem('openId', openId);
            setAuth(response.data.user, response.data.token);
            navigate(getRedirectPath());
          } else {
            console.log('[DEBUG] openId 登录失败:', response.error);
            setError(response.error || 'openId 登录失败');
          }
        })
        .catch((err: any) => {
          console.error('[DEBUG] authAPI.openidLogin 异常:', err);
          setError(err?.message || 'openId 验证失败，请使用账号密码登录');
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      console.log('[DEBUG] URL 中没有 openId 参数，正常显示登录表单');
    }
  }, [navigate, setAuth]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      let response;
      if (isLogin) {
        response = await authAPI.login(form.username, form.password);
      } else {
        response = await authAPI.register(form.username, form.email, form.password);
      }

      if (response.success && response.data) {
        setAuth(response.data.user, response.data.token);
        navigate(getRedirectPath());
      } else {
        setError(response.error || '操作失败');
      }
    } catch (err: any) {
      setError(err?.message || err?.response?.data?.message || String(err) || '操作失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-2xl shadow-lg mb-4">
            <Sparkles size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">栩栩AI</h1>
          <p className="text-gray-500 mt-1">市场线索分析助手</p>
        </div>

        {/* 表单卡片 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-semibold text-center mb-6">
            {isLogin ? '欢迎回来' : '创建账号'}
          </h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                用户名
              </label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="input"
                placeholder="请输入用户名"
                required
              />
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  邮箱
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="input"
                  placeholder="请输入邮箱"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                密码
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="input pr-10"
                  placeholder="请输入密码"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'w-full py-2.5 rounded-lg font-medium text-white transition-all',
                'bg-primary-500 hover:bg-primary-600',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center justify-center gap-2'
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  处理中...
                </>
              ) : (
                isLogin ? '登录' : '注册'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {isLogin ? '还没有账号？立即注册' : '已有账号？立即登录'}
            </button>
          </div>
        </div>

        {/* 底部信息 */}
        <p className="text-center text-sm text-gray-400 mt-8">
          © 2024 栩栩AI. All rights reserved.
        </p>
      </div>
    </div>
  );
}
